import { ProductTypeEnum } from "../../components/arps/models/ArpsSegment";

export const ratioProductListForForecasting = [
  "C2",
  "C3",
  "C4",
  "C5",
  "WGR",
  "CGR",
  "OGR"
];

export function productStringToProductTypeEnum(product: string): ProductTypeEnum {
  const known = tryProductStringToProductTypeEnum(product);
  if (known === undefined) {
    // eslint-disable-next-line no-console
    console.warn("unknown product: " + product);
    return ProductTypeEnum.Unknown;
  }
  return known;
}

/**
 * Same mapping as productStringToProductTypeEnum, without the console.warn on
 * a miss -- for call sites that already treat "unmapped" as an expected,
 * silently-handled case (e.g. falling back to an unfiltered call) rather than
 * a logged anomaly. Returns undefined instead of ProductTypeEnum.Unknown so a
 * miss can't be mistaken for a resolved "Unknown" product type.
 *
 * arps-wasm's src/ffi.rs (`product_type`) mirrors this mapping for the forecast
 * arrays eva-forecasting-svc stamps on publish; keep the two in sync.
 */
export function tryProductStringToProductTypeEnum(
  product: string
): ProductTypeEnum | undefined {
  switch (product.replace(".", "").toLocaleLowerCase()) {
    case "oil":
      return ProductTypeEnum.Oil;
    case "cond":
    case "condensate":
      return ProductTypeEnum.Oil;
    case "gas":
      return ProductTypeEnum.Gas;
    case "water":
      return ProductTypeEnum.Water;
    case "steam":
      return ProductTypeEnum.Steam;
    case "gor":
      return ProductTypeEnum.GOR;
    case "cgr":
      return ProductTypeEnum.CGR;
    case "ogr":
      return ProductTypeEnum.OGR;
    case "wgr":
      return ProductTypeEnum.WGR;
    case "wor":
      return ProductTypeEnum.WOR;
    case "1+wor":
    case "inverse_oil_cut":
      return ProductTypeEnum.OnePlusWOR;
    case "oil_plus_water":
    case "oilpluswater":
    case "o+w":
      return ProductTypeEnum.OilPlusWater;
    case "oil cut":
      return ProductTypeEnum.OilCut;
    case "water cut":
      return ProductTypeEnum.WaterCut;
    case "sales gas":
      return ProductTypeEnum.SalesGas;
    case "shrinkage":
      return ProductTypeEnum.Shrinkage;
    case "c2":
      return ProductTypeEnum.C2;
    case "c2yieldsales":
    case "ethyieldsales":
      return ProductTypeEnum.C2YieldSales;
    case "c3":
      return ProductTypeEnum.C3;
    case "c3yieldsales":
    case "propyieldsales":
      return ProductTypeEnum.C3YieldSales;
    case "c4":
      return ProductTypeEnum.C4;
    case "c4yieldsales":
    case "butyieldsales":
      return ProductTypeEnum.C4YieldSales;
    case "pent+":
    case "c5":
      return ProductTypeEnum.C5;
    case "c5yieldsales":
    case "pent+yieldsales":
      return ProductTypeEnum.C5YieldSales;
    case "on-time":
      return ProductTypeEnum.OnTime;
    case "heatingvalue":
    case "hv":
      return ProductTypeEnum.HeatingValue;
    case "sor":
      return ProductTypeEnum.SOR;
    default:
      return undefined;
  }
}

export function getDefaultRateUnits(product: ProductTypeEnum): string {
  switch (product) {
    case ProductTypeEnum.Oil:
    case ProductTypeEnum.Cond:
    case ProductTypeEnum.Water:
    case ProductTypeEnum.Steam:
      return "bbl/d";
    case ProductTypeEnum.Gas:
    case ProductTypeEnum.SalesGas:
      return "mcf/d";
    case ProductTypeEnum.GOR:
      return "scf/bbl";
    case ProductTypeEnum.WOR:
    case ProductTypeEnum.OnePlusWOR:
    case ProductTypeEnum.SOR:
      return "bbl/bbl";
    case ProductTypeEnum.CGR:
    case ProductTypeEnum.OGR:
    case ProductTypeEnum.WGR:
    case ProductTypeEnum.C2:
    case ProductTypeEnum.C3:
    case ProductTypeEnum.C4:
    case ProductTypeEnum.C5:
      return "bbl/MMcf";
    case ProductTypeEnum.OilPlusWater:
      return "bbl/d";
    case ProductTypeEnum.Shrinkage:
      return "%";
    case ProductTypeEnum.OnTime:
      return "%";
    case ProductTypeEnum.HeatingValue:
      return "Btu/scf";
  }
}

export function getDefaultRateUnitsFromString(product: string): string {
  switch (product.toLowerCase()) {
    case "oil":
    case "cond":
    case "water":
    case "steam":
      return "bbl/d";
    case "gas":
      return "mcf/d";
    case "gor":
      return "scf/bbl";
    case "wor":
    case "onepluswor":
    case "1+wor":
    case "inverse_oil_cut":
    case "sor":
      return "bbl/bbl";
    case "cgr":
    case "ogr":
    case "wgr":
    case "c2":
    case "c3":
    case "c4":
    case "c5":
      return "bbl/MMcf";
    case "oil_plus_water":
    case "oilpluswater":
    case "o+w":
      return "bbl/d";
    case "shrinkage":
      return "%";
    case "on-time":
      return "%";
    case "heatingvalue":
    case "hv":
      return "Btu/scf";
  }
}

export function getDatabaseRateUnits(product: ProductTypeEnum): string {
  switch (product) {
    case ProductTypeEnum.Oil:
    case ProductTypeEnum.Cond:
    case ProductTypeEnum.Water:
    case ProductTypeEnum.Steam:
      return "bbl/d";
    case ProductTypeEnum.Gas:
    case ProductTypeEnum.SalesGas:
      return "mcf/d";
    case ProductTypeEnum.GOR:
      return "Mcf/bbl";
    case ProductTypeEnum.WOR:
    case ProductTypeEnum.OnePlusWOR:
    case ProductTypeEnum.SOR:
      return "bbl/bbl";
    case ProductTypeEnum.CGR:
    case ProductTypeEnum.OGR:
    case ProductTypeEnum.WGR:
    case ProductTypeEnum.C2:
    case ProductTypeEnum.C2YieldSales:
    case ProductTypeEnum.C3:
    case ProductTypeEnum.C3YieldSales:
    case ProductTypeEnum.C4:
    case ProductTypeEnum.C4YieldSales:
    case ProductTypeEnum.C5:
    case ProductTypeEnum.C5YieldSales:
      return "bbl/MMcf";
    case ProductTypeEnum.OilPlusWater:
      return "bbl/d";
    case ProductTypeEnum.Shrinkage:
    case ProductTypeEnum.OnTime:
      return "%";
    case ProductTypeEnum.HeatingValue:
      return "Btu/scf";
    default:
      return "";
  }
}

export function getDefaultCumUnits(product: ProductTypeEnum, isEur: boolean) {
  switch (product) {
    case ProductTypeEnum.Oil:
    case ProductTypeEnum.Water:
    case ProductTypeEnum.OilPlusWater:
    case ProductTypeEnum.Steam:
      return "Mbbl";
    case ProductTypeEnum.Gas:
    case ProductTypeEnum.SalesGas:
      return "MMcf";
    case ProductTypeEnum.GOR:
    case ProductTypeEnum.WOR:
    case ProductTypeEnum.OnePlusWOR:
    case ProductTypeEnum.SOR:
      return isEur ? "MMcf" : "Mbbl";
    case ProductTypeEnum.CGR:
    case ProductTypeEnum.WGR:
      return isEur ? "Mbbl" : "MMcf";
  }
}

export function getDefaultDeclineUnits(product: ProductTypeEnum) {
  switch (product) {
    case ProductTypeEnum.Oil:
    case ProductTypeEnum.Water:
    case ProductTypeEnum.Gas:
    case ProductTypeEnum.SalesGas:
    case ProductTypeEnum.Steam:
      return "%/yr";
    default:
      return "%/vol";
  }
}
